import type { Section } from 'components/editor/Sections/Section.model';
import type { VariableNode } from 'services/entities/VariablesEntity';
import type { TableBlockStateType } from './editorSlice.types';
import type { TableType } from './table.types';
import { ContentType } from 'utils/getContentType';
import {
  deriveCrmRows,
  findReferencedArrayNodes,
  isGroupedArrayNode,
  pruneRowsForUnlinkedArrays,
} from 'components/editor/GridDndEditor/Block/Table/tableUtils/deriveCrmRows';
import { deriveGroupRows } from 'components/editor/GridDndEditor/Block/Table/tableUtils/deriveGroupRows';
import { clearStaleConflictFlags } from 'components/editor/GridDndEditor/Block/Table/crmArrayPaste/crmArrayConflict';

export type ComputeCrmCountSyncUpdatesParams = {
  isLocked: boolean;
  crmFetchFailed: boolean;
  sections: Section[];
  variablesRoot: VariableNode | undefined;
  isGroupRowEnabled: boolean;
};

export type SyncCrmRowsParams = ComputeCrmCountSyncUpdatesParams & {
  contentType: ContentType;
  crmPricingEmbedEnabled: boolean;
  crmVariablesV2RouteEnabled: boolean;
  handleTableContentChanged: (blockId: string, sectionId: string, previousContent: TableType, newContent: TableType) => void;
};

export function syncCrmRows(params: SyncCrmRowsParams): void {
  const { contentType, crmPricingEmbedEnabled, crmVariablesV2RouteEnabled, handleTableContentChanged, sections, ...computeParams } = params;

  if (contentType === ContentType.SECTION || contentType === ContentType.TEMPLATE) return;
  if (!crmPricingEmbedEnabled || !crmVariablesV2RouteEnabled) return;

  const updates = computeCrmCountSyncUpdates({ ...computeParams, sections });

  updates.forEach((update) => {
    const previousContentTable = sections.find((section) => section.id === update.sectionId)?.blocksContent[update.blockId]?.contentTable;
    if (!previousContentTable) return;
    handleTableContentChanged(update.blockId, update.sectionId, previousContentTable, update.contentTable);
  });
}

export function computeCrmCountSyncUpdates(params: ComputeCrmCountSyncUpdatesParams): TableBlockStateType[] {
  const { isLocked, crmFetchFailed, sections, variablesRoot, isGroupRowEnabled } = params;
  if (isLocked || crmFetchFailed || !variablesRoot) return [];

  const allArrayNodes = (variablesRoot.variables ?? []).flatMap(collectVariableNodes).filter(isGroupedArrayNode);
  if (allArrayNodes.length === 0) return [];

  const updates: TableBlockStateType[] = [];

  sections.forEach((section) => {
    Object.entries(section.blocksContent).forEach(([blockId, block]) => {
      const contentTable = block.contentTable;
      if (!contentTable) return;

      const rowsWithValidConflicts = contentTable.rows.map((row) => clearStaleConflictFlags(row, contentTable.columns));
      const rowsAfterPrune = pruneRowsForUnlinkedArrays(rowsWithValidConflicts, contentTable.columns, allArrayNodes);

      const referencedArrays = findReferencedArrayNodes(rowsAfterPrune, contentTable.columns, allArrayNodes);

      const flatRows = referencedArrays.reduce(
        (rows, arrayNode) => deriveCrmRows({ rows, columns: contentTable.columns, arrayNode }),
        rowsAfterPrune
      );
      const nextRows = deriveGroupRows({ rows: flatRows, columns: contentTable.columns, variablesRoot, enabled: isGroupRowEnabled });

      if (nextRows === contentTable.rows || areRowsEqual(nextRows, contentTable.rows)) return;

      updates.push({ sectionId: section.id, blockId, contentTable: { ...contentTable, rows: nextRows } });
    });
  });

  return updates;
}

export function pruneOrphanedCrmRowsAcrossSections(sections: Section[], allArrayNodes: VariableNode[]): TableBlockStateType[] {
  const updates: TableBlockStateType[] = [];

  sections.forEach((section) => {
    Object.entries(section.blocksContent).forEach(([blockId, block]) => {
      const contentTable = block.contentTable;
      if (!contentTable) return;

      const prunedRows = pruneRowsForUnlinkedArrays(contentTable.rows, contentTable.columns, allArrayNodes);
      if (prunedRows === contentTable.rows) return;

      updates.push({ sectionId: section.id, blockId, contentTable: { ...contentTable, rows: prunedRows } });
    });
  });

  return updates;
}

export function collectVariableNodes(node: VariableNode): VariableNode[] {
  return [node, ...(node.variables ?? []).flatMap(collectVariableNodes)];
}

function areRowsEqual(a: TableType['rows'], b: TableType['rows']): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
